import * as PropTypes from "prop-types";
import Button from "@mui/material/Button";
import React, { useEffect, useLayoutEffect } from "react";
import { Box, Stack } from '@mui/material';
import { defaultChannelGroups } from "./channelPage";
import { useDispatch } from "react-redux";
import { setCurrentChannelPage } from "../../model/slice/channelSlice";
import { getChannelGroup } from "../../api/channel";
import { AddButton } from "../common/addButton";

const MAX_CHANNEL_GROUPS_COUNT = 16;

const buttonDesign = {
  alignItems: "left",
  justifyContent: "flex-start",
  display: "flex",
  width: "fitContent",
  "&:focus": {
    outline: "none",
  },
};

const buttonTextDesign = {
  color: "#ffffff",
  fontFamily: "Inter",
  fontSize: "11px",
  fontWeight: "300",
  fontStretch: "normal",
  fontStyle: "normal",
  lineHeight: "normal",
  letterSpacing: "normal",
  textTransform: "none",
  opacity: "100%",
  justifyContent: "flex-start",
};
const trueButton = {
    ...buttonDesign,
    "& span": {
      ...buttonTextDesign,
      color: "#19b2ab",
      fontWeight: "600",
    },
    borderRadius: 0,
};
const falseButton = {
    ...buttonDesign,
    minWidth: '0px',
    "& span": {
      ...buttonTextDesign,
      color: "#ffffff",
      "&:hover": {
        opacity: "70%",
      },
    },
  }

const ChannelGroupSelector = React.memo(
  ({
    vesselId,
    selectedChannelGroup,
    setSelectedChannelGroup,
    isCreatingNewGroup,
    setIsCreatingNewGroup,
    previousSelectedChannelGroup,
    channelGroups,
    setChannelGroups,
    isEditingGroup,
    setIsEditingGroup,
    setSelectedChannels,
    isUpdateFetch,
    setIsUpdateFetch,
  }) => {
    const dispatch = useDispatch();
    const resetPage = () => dispatch(setCurrentChannelPage(0));
    const isDisabledNewButton = channelGroups.length >= MAX_CHANNEL_GROUPS_COUNT;

    useLayoutEffect(() => {
      fetchChannelGroup();

      return () => {
        setIsUpdateFetch(false);
      }
    }, [vesselId])

    useEffect(() => {
      if(!isUpdateFetch) return;
      fetchChannelGroup();
    }, [isUpdateFetch])

    const fetchChannelGroup = async () => {
      const data = await getChannelGroup(vesselId);
      const existGroup = data?.channelGroupData;
      if(existGroup) {
        const channelGroups = [...defaultChannelGroups, ...existGroup];
        setChannelGroups(channelGroups);
        if(isCreatingNewGroup) {
          setSelectedChannelGroup(channelGroups.at(-1));
          setIsCreatingNewGroup(false);
        }
        if(!isCreatingNewGroup && !channelGroups.find((group) => group.id === selectedChannelGroup.id)) {
          resetToDefaultGroups()
        }
      }else{
        setChannelGroups(defaultChannelGroups);
        resetToDefaultGroups();
      }
      setIsUpdateFetch(false);
    }

    const resetToDefaultGroups = () => {
      setSelectedChannelGroup(defaultChannelGroups[0]);
      setSelectedChannels([]);
      setIsCreatingNewGroup(false);
      setIsEditingGroup(false);
    }

    const changeSelectedGroup = (group) => {
      if(isCreatingNewGroup) {
        setChannelGroups(channelGroups.slice(0,-1));
        setIsCreatingNewGroup(false);
      }
      if(isEditingGroup) {
        setIsEditingGroup(false);
      }
      resetPage();
      setSelectedChannels([]);
      setSelectedChannelGroup(group);
      previousSelectedChannelGroup.current = group;
    }

    const openGroupRegisterPage = () => {
      if(isCreatingNewGroup) return;
      const newChannelGroup = {name: `New Custom Group-${channelGroups.length}`, values: []};
      previousSelectedChannelGroup.current = selectedChannelGroup;
      setIsCreatingNewGroup(true);
      setSelectedChannels([]);
      setChannelGroups([...channelGroups, newChannelGroup]);
      setSelectedChannelGroup(newChannelGroup);
    }

    return (
      <Stack
        spacing={2}
        justifyContent="flex-start"
        direction="row"
        alignItems="center"
        sx={{color: 'white', width: '90%', overflowY: 'auto', scrollbarWidth: 'none'}}
        flexWrap={"wrap"}
      >
        {
          channelGroups.map((group, i) => 
            <Button 
              key={group.id} 
              sx={group.id === selectedChannelGroup.id ? trueButton : falseButton} 
              onClick={(e) => changeSelectedGroup(group)}
            >
              <Box component="span">{group.name}</Box>
            </Button>
          )
        }
        {
          !isCreatingNewGroup && <AddButton onClickHandler={() => openGroupRegisterPage()} disabled={isDisabledNewButton} />
        }
      </Stack>
    );
  }
);

ChannelGroupSelector.propTypes = {
  vesselId: PropTypes.string,
  channelGroups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      values: PropTypes.array.isRequired
    })
  ).isRequired,
  setChannelGroups: PropTypes.func.isRequired,
  selectedChannelGroup: PropTypes.string.isRequired,
  setSelectedChannelGroup: PropTypes.func.isRequired,
  isCreatingNewGroup: PropTypes.bool.isRequired,
  setIsCreatingNewGroup: PropTypes.func.isRequired,
  previousSelectedChannelGroup: PropTypes.object,
  isEditingGroup: PropTypes.bool.isRequired,
  setIsEditingGroup: PropTypes.func.isRequired,
  setSelectedChannels: PropTypes.func.isRequired,
  isUpdateFetch: PropTypes.bool.isRequired,
  setIsUpdateFetch: PropTypes.func.isRequired,
};

export default ChannelGroupSelector;
